import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "../../utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2FormPageDarkSelectStyles,
} from "utils/theme2MultiSelect";
import { useCreateDeal } from "../../api/deals/service/index";
import IdentityInformationForm from "../../components/Customers/IdentityInformationForm";
import { useGetChannels } from "api/channels/service";
import Select from "components/Select";
import classNames from "classnames";
import {
  SALES_GROUP_PERSON_PAGE_SIZE,
  getSalesPersonOptions,
  getSalesManagerOptions,
  getFiPersonOptions,
  findSalesGroupPersonName,
} from "utils/salesGroupPersonOptions";

const AddRetailDeal = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const isTheme2 = shellThemeVariant === "2";
  const createDealMutation = useCreateDeal();
  const [currentStep, setCurrentStep] = useState(1);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".add-retail-deal-page.add-retail-deal-page--theme2",
            fieldsSelector: ".add-retail-deal-form-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: "14px" } : undefined;
  const theme2SelectMenuClassName = isTheme2 ? "add-retail-deal-select-menu" : undefined;
  const theme2SelectProps = theme2SelectMenuClassName
    ? {
        classNames: {
          menu: () => theme2SelectMenuClassName,
          menuPortal: () => theme2SelectMenuClassName,
        },
      }
    : {};
  const [dealData, setDealData] = useState({
    application_type: "Individual Application",
    deal_origination: "Unknown",
    customerHearAbout: "Unknown",
    referral_source: "",
    desiredVehicle: "",
    sales_person_1: "",
    sales_person_1_commission: "0",
    sales_person_2: "",
    sales_person_2_commission: "0",
    sales_manager: "",
    fi_person: "",
    closing_probability: "50",
  });
  const { data: salesGroupPersons } = useGetChannels("sales_group_person", 1, SALES_GROUP_PERSON_PAGE_SIZE);
  const { data: leadReferralSources } = useGetChannels("lead_referral_source", 1, SALES_GROUP_PERSON_PAGE_SIZE);
  const salesGroupPersonResults = salesGroupPersons?.results || [];
  const salesRoleOptions = React.useMemo(() => getSalesPersonOptions(salesGroupPersonResults), [salesGroupPersonResults]);
  const managerRoleOptions = React.useMemo(() => getSalesManagerOptions(salesGroupPersonResults), [salesGroupPersonResults]);
  const fiRoleOptions = React.useMemo(() => getFiPersonOptions(salesGroupPersonResults), [salesGroupPersonResults]);
  const [focusedField, setFocusedField] = useState(null);

  const [identityData, setIdentityData] = useState({
    ssn: "",
    date_of_birth: "",
    full_name: "",
    gender: "",
    active_service_member: false,
    new_customer: true,
    customer_id: null,
  });

  const isFormValid = () => {
    return identityData.full_name.trim() !== "";
  };

  const handleDealInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'sales_person_1_commission' || name === 'sales_person_2_commission' || name === 'closing_probability') {
      // % işaretini kaldır
      let cleanValue = value.replace('%', '');
      let numValue = parseFloat(cleanValue);

      // 100'den büyük değerleri engelle
      if (numValue > 100) {
        numValue = 100;
      }

      // Negatif değerleri engelle
      if (numValue < 0) {
        numValue = 0;
      }

      setDealData(prev => ({
        ...prev,
        [name]: numValue.toString()
      }));
    } else {
      setDealData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const handleIdentityInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setIdentityData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const nextStep = () => {
    setCurrentStep(2);
  };

  const prevStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = (e, identityOverride = null) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    let formattedDate = null;

    const identity = identityOverride || identityData;

    const dealDataForAPI = {
      application_type: dealData.application_type,
      deal_origination: dealData.deal_origination,
      referral_source: dealData.referral_source === "" ? null : Number(dealData.referral_source),
      customer_hear_about: dealData.customerHearAbout,
      sales_person_1: dealData.sales_person_1 === "" ? null : Number(dealData.sales_person_1),
      sales_person_1_commission: parseFloat(dealData.sales_person_1_commission),
      sales_person_2: dealData.sales_person_2 === "" ? null : Number(dealData.sales_person_2),
      sales_person_2_commission: parseFloat(dealData.sales_person_2_commission),
      sales_manager: dealData.sales_manager === "" ? null : Number(dealData.sales_manager),
      fi_person: dealData.fi_person === "" ? null : Number(dealData.fi_person),
      closing_probability: parseFloat(dealData.closing_probability),
      deal_type: "retail_deal",
      applicant: {
        new_customer: identity.new_customer,
        ssn: identity.ssn,
        date_of_birth: formattedDate,
        full_name: identity.full_name,
        gender: identity.gender,
        active_service_member: identity.active_service_member,
        customer_id: identity.customer_id,
      }
    };

    createDealMutation.mutate(dealDataForAPI);
  };

  const renderDealStep = () => (
    <>
      <Row>
        <Col md="12">
          <h5 className="mb-2 mt-0">{t('addRetailDeal.dealInformation')}</h5>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="application_type">{t('addRetailDeal.applicationType')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.applicationType')}
              name="application_type"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={{
                value: dealData.application_type,
                label: dealData.application_type === "Individual Application"
                  ? t('addRetailDeal.individualApplication')
                  : t('addRetailDeal.jointApplication')
              }}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, application_type: option.value }));
              }}
              options={[
                { value: "Individual Application", label: t('addRetailDeal.individualApplication') },
                { value: "Joint Application", label: t('addRetailDeal.jointApplication') }
              ]}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="deal_origination">{t('addRetailDeal.dealOrigination')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.dealOrigination')}
              name="deal_origination"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={{
                value: dealData.deal_origination,
                label: dealData.deal_origination === "Unknown" ? t('addRetailDeal.unknown') :
                  dealData.deal_origination === "Walk-in" ? t('addRetailDeal.walkIn') :
                    dealData.deal_origination === "Phone" ? t('addRetailDeal.phone') :
                      dealData.deal_origination === "Internet" ? t('addRetailDeal.internet') :
                        dealData.deal_origination === "Referral" ? t('addRetailDeal.referral') :
                          dealData.deal_origination === "Previous Customer" ? t('addRetailDeal.previousCustomer') :
                            dealData.deal_origination === "Trade-in" ? t('addRetailDeal.tradeIn') :
                              dealData.deal_origination === "Service Department" ? t('addRetailDeal.serviceDepartment') :
                                dealData.deal_origination
              }}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, deal_origination: option.value }));
              }}
              options={[
                { value: "Unknown", label: t('addRetailDeal.unknown') },
                { value: "Walk-in", label: t('addRetailDeal.walkIn') },
                { value: "Phone", label: t('addRetailDeal.phone') },
                { value: "Internet", label: t('addRetailDeal.internet') },
                { value: "Referral", label: t('addRetailDeal.referral') },
                { value: "Previous Customer", label: t('addRetailDeal.previousCustomer') },
                { value: "Trade-in", label: t('addRetailDeal.tradeIn') },
                { value: "Service Department", label: t('addRetailDeal.serviceDepartment') }
              ]}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="customerHearAbout">{t('addRetailDeal.howDidCustomerHearAboutYou')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.howDidCustomerHearAboutYou')}
              name="customerHearAbout"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={{
                value: dealData.customerHearAbout,
                label: dealData.customerHearAbout === "Unknown" ? t('addRetailDeal.unknown') :
                  dealData.customerHearAbout === "Advertisement / Referral Channel" ? t('addRetailDeal.advertisementReferralChannel') :
                    dealData.customerHearAbout === "Customer" ? t('addRetailDeal.customer') :
                      dealData.customerHearAbout
              }}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, customerHearAbout: option.value }));
              }}
              options={[
                { value: "Unknown", label: t('addRetailDeal.unknown') },
                { value: "Advertisement / Referral Channel", label: t('addRetailDeal.advertisementReferralChannel') },
                { value: "Customer", label: t('addRetailDeal.customer') }
              ]}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="referralSource">{t('addRetailDeal.referralSource')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.enterReferralSource')}
              name="referral_source"
              isSearchable
              closeMenuOnSelect={true}
              value={dealData.referral_source ? {
                value: dealData.referral_source,
                label: leadReferralSources?.results?.find(source => String(source.id) === dealData.referral_source)?.name || dealData.referral_source
              } : null}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, referral_source: option?.value || "" }));
              }}
              options={leadReferralSources?.results?.map((source) => ({
                value: String(source.id),
                label: source.name
              })) || []}
              isClearable
              isDisabled={dealData.customerHearAbout !== "Advertisement / Referral Channel"}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="desiredVehicle">{t('addRetailDeal.desiredVehicle')}</Label>
            <Input
              type="text"
              name="desiredVehicle"
              id="desiredVehicle"
              value={dealData.desiredVehicle}
              onChange={handleDealInputChange}
              className="form-control"
              style={inputStyle}
              placeholder={t('addRetailDeal.enterDesiredVehicle')}
              disabled
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Assignment Information */}
      <Row>
        <Col md="12">
          <h5 className="mb-2 mt-0">{t('addRetailDeal.assignmentInformation')}</h5>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="sales_person_1">{t('addRetailDeal.salesPerson1')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.salesPerson1')}
              name="sales_person_1"
              isSearchable
              closeMenuOnSelect={true}
              value={dealData.sales_person_1 ? {
                value: dealData.sales_person_1,
                label: findSalesGroupPersonName(salesGroupPersonResults, dealData.sales_person_1)
              } : null}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, sales_person_1: option?.value || "" }));
              }}
              options={[
                { value: "", label: t('addRetailDeal.notAssigned') },
                ...salesRoleOptions.map((p) => ({
                  value: String(p.id),
                  label: p.name
                }))
              ]}
              isClearable
            />
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label for="sales_person_1_commission">{t('addRetailDeal.salesPerson1Commission')}</Label>
            <Input
              type="text"
              name="sales_person_1_commission"
              id="sales_person_1_commission"
              value={focusedField === 'sales_person_1_commission' ? dealData.sales_person_1_commission : dealData.sales_person_1_commission + '%'}
              onChange={handleDealInputChange}
              onFocus={() => handleFocus('sales_person_1_commission')}
              onBlur={handleBlur}
              className="form-control"
              style={inputStyle}
              placeholder="0%"
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="sales_person_2">{t('addRetailDeal.salesPerson2')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.salesPerson2')}
              name="sales_person_2"
              isSearchable
              closeMenuOnSelect={true}
              value={dealData.sales_person_2 ? {
                value: dealData.sales_person_2,
                label: findSalesGroupPersonName(salesGroupPersonResults, dealData.sales_person_2)
              } : null}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, sales_person_2: option?.value || "" }));
              }}
              options={[
                { value: "", label: t('addRetailDeal.notAssigned') },
                ...salesRoleOptions.map((p) => ({
                  value: String(p.id),
                  label: p.name
                }))
              ]}
              isClearable
            />
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label for="sales_person_2_commission">{t('addRetailDeal.salesPerson2Commission')}</Label>
            <Input
              type="text"
              name="sales_person_2_commission"
              id="sales_person_2_commission"
              value={focusedField === 'sales_person_2_commission' ? dealData.sales_person_2_commission : dealData.sales_person_2_commission + '%'}
              onChange={handleDealInputChange}
              onFocus={() => handleFocus('sales_person_2_commission')}
              onBlur={handleBlur}
              className="form-control"
              style={inputStyle}
              placeholder="0%"
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="sales_manager">{t('addRetailDeal.salesManager')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.salesManager')}
              name="sales_manager"
              isSearchable
              closeMenuOnSelect={true}
              value={dealData.sales_manager ? {
                value: dealData.sales_manager,
                label: findSalesGroupPersonName(salesGroupPersonResults, dealData.sales_manager)
              } : null}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, sales_manager: option?.value || "" }));
              }}
              options={[
                { value: "", label: t('addRetailDeal.notAssigned') },
                ...managerRoleOptions.map((p) => ({
                  value: String(p.id),
                  label: p.name
                }))
              ]}
              isClearable
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="fi_person">{t('addRetailDeal.fIPerson')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              {...theme2SelectProps}
              placeholder={t('addRetailDeal.fIPerson')}
              name="fi_person"
              isSearchable
              closeMenuOnSelect={true}
              value={dealData.fi_person ? {
                value: dealData.fi_person,
                label: findSalesGroupPersonName(salesGroupPersonResults, dealData.fi_person)
              } : null}
              onChange={(option) => {
                setDealData(prev => ({ ...prev, fi_person: option?.value || "" }));
              }}
              options={[
                { value: "", label: t('addRetailDeal.notAssigned') },
                ...fiRoleOptions.map((p) => ({
                  value: String(p.id),
                  label: p.name
                }))
              ]}
              isClearable
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="closing_probability">{t('addRetailDeal.closingProbability')}</Label>
            <Input
              type="text"
              name="closing_probability"
              id="closing_probability"
              value={focusedField === 'closing_probability' ? dealData.closing_probability : dealData.closing_probability + '%'}
              onChange={handleDealInputChange}
              onFocus={() => handleFocus('closing_probability')}
              onBlur={handleBlur}
              className="form-control"
              style={inputStyle}
              placeholder="50%"
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );

  const renderIdentityStep = () => (
    <IdentityInformationForm
      identityData={identityData}
      onChange={handleIdentityInputChange}
      showValidation={true}
      handleSubmit={handleSubmit}
      title={t('addRetailDeal.identityInformation')}
      activeColor={activeColor}
      isTheme2={isTheme2}
      selectStyles={selectStyles}
      theme2SelectMenuClassName={theme2SelectMenuClassName}
      onCustomerSelect={(customerId) => {
        // Submit immediately with forced values — bypasses async state
        handleSubmit(null, {
          ...identityData,
          new_customer: false,
          customer_id: customerId,
        });
      }}
    />
  );

  return (
    <div
      className={classNames("content add-retail-deal-page", {
        "add-retail-deal-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-add-retail-deal-inset": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <style>
        {`
          .add-retail-deal-page > .card > .add-retail-deal-card-header.card-header {
            padding-top: 2px !important;
            padding-bottom: 0 !important;
          }
          .add-retail-deal-page > .card > .add-retail-deal-card-header h2,
          .add-retail-deal-page > .card > .add-retail-deal-card-header h4 {
            font-size: 16px !important;
            margin-top: 10px;
            margin-bottom: 0 !important;
            line-height: 1.2;
          }
          .add-retail-deal-page > .card > .card-body {
            padding: 15px !important;
            padding-top: 10px !important;
            padding-bottom: 0 !important;
          }
          .add-retail-deal-page .add-retail-deal-actions {
            margin-top: 8px;
            margin-bottom: 18px;
          }
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 label,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 h2,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 h5,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .form-control,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .form-text,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 th,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 td,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .alert,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .alert strong,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__single-value,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__input-container,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__placeholder,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 thead.text-primary,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 thead.text-primary th {
            color: rgba(255, 255, 255, 0.95) !important;
            font-weight: 400;
          }
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 h2,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 h5,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .form-group:focus-within > label,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .form-group:focus-within label.form-check-label,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .form-control:focus,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .form-control:not(:placeholder-shown),
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__control--is-focused .react-select__single-value,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__control:has(.react-select__single-value) .react-select__single-value {
            font-weight: 500 !important;
          }
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__dropdown-indicator,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__clear-indicator {
            color: rgba(255, 255, 255, 0.4) !important;
          }
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__dropdown-indicator:hover,
          body:not(.white-content) .add-retail-deal-page.add-retail-deal-page--theme2 .react-select__clear-indicator:hover {
            color: rgba(255, 255, 255, 0.65) !important;
          }
          body:not(.white-content) .add-retail-deal-select-menu .react-select__option,
          body:not(.white-content) .add-retail-deal-select-menu .react-select__menu-notice {
            color: rgba(255, 255, 255, 0.95) !important;
            font-weight: 400;
          }
          body:not(.white-content) .add-retail-deal-select-menu .react-select__option--is-selected,
          body:not(.white-content) .add-retail-deal-select-menu .react-select__option--is-focused {
            color: rgba(255, 255, 255, 0.95) !important;
            font-weight: 500 !important;
            background-color: rgba(255, 255, 255, 0.1) !important;
          }
          body:not(.white-content) .add-retail-deal-select-menu .react-select__option--is-focused:not(.react-select__option--is-selected) {
            background-color: rgba(255, 255, 255, 0.05) !important;
          }
        `}
      </style>
      <Card>
        <CardHeader className="add-retail-deal-card-header">
          <h2 className="mb-0">{t('addRetailDeal.title')}</h2>
        </CardHeader>
        <CardBody>
          <Form
            onSubmit={handleSubmit}
            className={classNames({ "add-retail-deal-form-fields": isTheme2 })}
          >
            {currentStep === 1 ? renderDealStep() : renderIdentityStep()}

            <Row className="add-retail-deal-actions">
              <Col md="12">
                    {currentStep === 1 ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        style={{ ...activeColorStyle, color: 'white' }}
                        disabled={createDealMutation.isLoading}
                        block
                      >
                        {t('addRetailDeal.addApplicant')}
                      </Button>
                    ) : (
                      <div className="d-flex justify-content-between">
                        <Button
                          type="button"
                          onClick={prevStep}
                          outline
                          color="secondary"
                        >
                          {t('addRetailDeal.back')}
                        </Button>
                        <Button
                          type="submit"
                          style={{ ...activeColorStyle, color: 'white' }}
                          disabled={createDealMutation.isLoading || !isFormValid()}
                        >
                          {createDealMutation.isLoading ? t('addRetailDeal.creatingDeal') : t('addRetailDeal.createRetailDeal')}
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddRetailDeal; 